// This is a skeleton starter React component generated by Plasmic.
// This file is owned by you, feel free to edit as you see fit.
import { COMMANDS } from "@/wab/client/commands/command";
import { StringPropEditor } from "@/wab/client/components/sidebar-tabs/ComponentProps/StringPropEditor";
import { PropEditorRow } from "@/wab/client/components/sidebar-tabs/PropEditorRow";
import { LabeledItemRow } from "@/wab/client/components/sidebar/sidebar-helpers";
import StyleSelect from "@/wab/client/components/style-controls/StyleSelect";
import { validateStateAccessType } from "@/wab/client/operations/utils/validate-state-changes";
import {
  DefaultNewVariableProps,
  PlasmicNewVariable,
} from "@/wab/client/plasmic/plasmic_kit_state_management/PlasmicNewVariable";
import { StudioCtx } from "@/wab/client/studio-ctx/StudioCtx";
import { ViewCtx } from "@/wab/client/studio-ctx/view-ctx";
import { wabTypeToPropType } from "@/wab/shared/code-components/code-components";
import { assert, spawn } from "@/wab/shared/common";
import { isPageComponent } from "@/wab/shared/core/components";
import { codeLit, getRawCode, tryExtractString } from "@/wab/shared/core/exprs";
import {
  NORMAL_STATE_VARIABLE_TYPES,
  NormalStateVariableType,
  StateAccessType,
  getAccessTypeDisplayName,
  isReadonlyState,
} from "@/wab/shared/core/states";
import { evalCodeWithEnv } from "@/wab/shared/eval";
import {
  Component,
  Expr,
  State,
  isKnownTplSlot,
} from "@/wab/shared/model/classes";
import { convertVariableTypeToWabType } from "@/wab/shared/model/model-util";
import { HTMLElementRefOf } from "@plasmicapp/react-web";
import { notification } from "antd";
import L from "lodash";
import { observer } from "mobx-react";
import * as React from "react";

export const PREVIEW_DISABLED_TOOLTIP_MESSAGE: Record<
  Exclude<StateAccessType, "private">,
  string
> = {
  writable:
    "For the read and write states, the initial value depends on the variant. Therefore, you should set them in the settings tab.",
  readonly: "It is not possible to update a read-only state.",
};

export interface VariableValueEditorProps {
  state: State;
  component: Component;
  studioCtx: StudioCtx;
  viewCtx: ViewCtx;
  disableInitialValue?: boolean;
  initialValueAbout?: string;
}

export interface NewVariableProps extends DefaultNewVariableProps {
  state: State;
  component: Component;
  studioCtx: StudioCtx;
  viewCtx: ViewCtx;
  mode?: "new" | "edit";
  onCancel?: () => void;
  onConfirm?: () => void;
}

export const VariableValueEditor = observer(function VariableValueEditor({
  component,
  state,
  studioCtx,
  viewCtx,
  disableInitialValue = false,
  initialValueAbout,
}: VariableValueEditorProps) {
  assert(
    !isKnownTplSlot(component.tplTree),
    "slots can't be root of a component"
  );
  const [previewDraft, setPreviewDraft] = React.useState<Expr | undefined>(
    undefined
  );
  const initialValue = viewCtx.getStateCurrentInitialValue(state);
  const currentValue = viewCtx.getCanvasStateValue(state);
  const hasTempValue = initialValue !== currentValue;
  const previewExpr = previewDraft ?? codeLit(currentValue);
  const propType = wabTypeToPropType(
    convertVariableTypeToWabType(state.variableType)
  );

  const env = viewCtx.getCanvasEnvForTpl(component.tplTree) ?? {};

  return (
    <div
      style={{ position: "relative", width: "100%" }}
      className={"flex-col flex-fill"}
    >
      <div className={"mb-m"}>
        <PropEditorRow
          viewCtx={viewCtx}
          tpl={component.tplTree}
          label={"Initial Value"}
          attr="initial-value"
          expr={state.param.defaultExpr ?? undefined}
          definedIndicator={{ source: "none" }}
          valueSetState={"isSet"}
          propType={propType}
          onChange={async (expr) => {
            const invalidMessage = validateStateAccessType(
              state.accessType,
              expr
            );
            if (invalidMessage) {
              notification.error({
                message: "Cannot set initial value",
                description: invalidMessage,
              });
              return;
            }
            await COMMANDS.component.changeStateInitialValue.execute(
              studioCtx,
              { expr },
              { state }
            );
          }}
          layout={"vertical"}
          disableLinkToProp={true}
          about={initialValueAbout}
          disabled={disableInitialValue}
        />
      </div>
      <PropEditorRow
        viewCtx={viewCtx}
        tpl={component.tplTree}
        label="Preview value"
        attr="preview-value"
        about={
          state.implicitState && isReadonlyState(state.implicitState)
            ? PREVIEW_DISABLED_TOOLTIP_MESSAGE[state.implicitState.accessType]
            : `Temporarily set a value for this variable to preview how your component would look or behave.`
        }
        disabled={
          state.implicitState ? isReadonlyState(state.implicitState) : false
        }
        expr={previewExpr}
        definedIndicator={
          hasTempValue && previewExpr
            ? {
                source: "setNonVariable",
                prop: "preview-value",
                value: tryExtractString(previewExpr) || "",
              }
            : { source: "none" }
        }
        valueSetState={"isSet"}
        propType={propType}
        onChange={(_expr) => {
          if (!_expr) {
            return;
          }
          setPreviewDraft(_expr);
          const code = getRawCode(_expr, {
            projectFlags: studioCtx.projectFlags(),
            component,
            inStudio: true,
          });
          const newValue = evalCodeWithEnv(code, env);
          viewCtx.setCanvasStateValue(state, newValue);
        }}
        onDelete={() => {
          setPreviewDraft(undefined);
          viewCtx.resetCanvasStateValue(state);
        }}
        layout={"vertical"}
        disableLinkToProp={true}
        disableDynamicValue={true}
      />
    </div>
  );
});

const VariableEditingForm = observer(
  React.forwardRef(function VariableEditingForm(
    {
      component,
      state,
      studioCtx,
      viewCtx,
      mode = "edit",
      onCancel,
      onConfirm,
      ...rest
    }: NewVariableProps,
    ref: HTMLElementRefOf<"div">
  ) {
    const StringEditor = React.useCallback(
      ({
        label,
        onChange,
        value,
        "data-plasmic-prop": dataPlasmicProp,
      }: {
        label: React.ReactNode;
        onChange: (val: string) => void;
        value: string;
        "data-plasmic-prop"?: string;
      }) => (
        <LabeledItemRow layout={"vertical"} label={label}>
          <StringPropEditor
            onChange={onChange}
            value={value}
            valueSetState={"isSet"}
            disabled={false}
            data-plasmic-prop={dataPlasmicProp}
          />
        </LabeledItemRow>
      ),
      []
    );

    const hasExternalAccess = state.accessType !== "private";
    return (
      <PlasmicNewVariable
        root={{ ref }}
        {...rest}
        variableName={
          <StringEditor
            label={state.implicitState ? "External name" : "Name"}
            onChange={(val) =>
              COMMANDS.component.changeStateVariableName.execute(
                studioCtx,
                {
                  varName: val,
                },
                {
                  state,
                  component,
                }
              )
            }
            data-plasmic-prop={"variable-name"}
            value={state.param.variable.name}
          />
        }
        variableType={{
          props: {
            value: state.variableType,
            "data-plasmic-prop": "variable-type",
            onChange: (val) =>
              COMMANDS.component.changeStateVariableType.execute(
                studioCtx,
                {
                  type: val as NormalStateVariableType | null,
                },
                {
                  state,
                }
              ),
            children: NORMAL_STATE_VARIABLE_TYPES.map((stateType) => (
              <StyleSelect.Option value={stateType} key={stateType}>
                {L.startCase(stateType)}
              </StyleSelect.Option>
            )),
          },
        }}
        isPageComponent={isPageComponent(component)}
        variableInitVal={
          <VariableValueEditor
            state={state}
            component={component}
            studioCtx={studioCtx}
            viewCtx={viewCtx}
          />
        }
        allowExternalAccess={{
          props: {
            isChecked: hasExternalAccess,
            onChange: (allow) => {
              spawn(
                COMMANDS.component.changeStateVariableAccessType.execute(
                  studioCtx,
                  {
                    accessType: allow ? "readonly" : "private",
                  },
                  {
                    state,
                  }
                )
              );
            },
            "data-test-id": "allow-external-access",
          },
        }}
        isExternal={hasExternalAccess}
        accessTypeSelect={{
          props: {
            "data-plasmic-prop": "access-type",
            value: state.accessType,
            onChange: async (val) => {
              const invalidMessage = validateStateAccessType(
                val as StateAccessType,
                state.param.defaultExpr
              );
              if (invalidMessage) {
                notification.error({
                  message: "Cannot set access type",
                  description: invalidMessage,
                });
                return;
              }
              await COMMANDS.component.changeStateVariableAccessType.execute(
                studioCtx,
                {
                  accessType: val as StateAccessType,
                },
                {
                  state,
                }
              );
            },
            children: (
              [
                {
                  value: "readonly",
                  label: getAccessTypeDisplayName("readonly"),
                },
                {
                  value: "writable",
                  label: getAccessTypeDisplayName("writable"),
                },
              ] as { value: StateAccessType; label: string }[]
            ).map(({ label, value }) => (
              <StyleSelect.Option value={value} key={value}>
                {label}
              </StyleSelect.Option>
            )),
          },
        }}
        accessType={
          state.accessType === "private"
            ? "_private"
            : (state.accessType as Exclude<StateAccessType, "private">)
        }
        isImplicitState={!!state.tplNode}
        withFormButtons={mode === "new"}
        cancelButton={{
          onClick: () => onCancel?.(),
        }}
        confirmButton={{
          props: {
            "data-test-id": "confirm",
            onClick: () => onConfirm?.(),
          },
        }}
      />
    );
  })
);
export default VariableEditingForm;
